import React, { useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Person as PersonIcon, Business as BusinessIcon } from "@mui/icons-material";
import { saveAuthData, getReturnPath } from "../../Utils/authHelper";
import CustomerSignupForm from "./CustomerSignupForm";
import BusinessSignupForm from "./BusinessSignupForm";
import { SwapTabs, SwapPanes } from "./SwapUI";
import BackToHome from "./BackToHome";
import { floatA, floatB, pulse, growLine, shake, stagger, pageSx, orbSx, sparkSx, cardSx } from "./signinStyles";

// One page for both kinds of account, same look and motion as sign in: a Customer | Business swap at the top.
// mode only chooses which one is open first: "customer" at /signup, "business" at /business/signup.
function Signup({ mode = "customer" }) {
  const navigate = useNavigate();
  const [accountType, setAccountType] = useState(mode === "business" ? "business" : "customer");
  const [apiError, setApiError] = useState("");
  const isCustomer = accountType === "customer";

  const handleSwap = (type) => {
    setAccountType(type);
    setApiError("");
    window.history.replaceState(null, "", type === "business" ? "/business/signup" : "/signup");
  };

  // both customer codes confirmed: the account is active and already signed in
  const handleCustomerSignedUp = (res) => {
    saveAuthData(res.token, res.customer, "common");
    navigate(getReturnPath(), { replace: true });
  };

  return (
    <Box sx={{ ...pageSx, height: "100vh", minHeight: 0, py: 1.5 }}>
      <BackToHome />
      <Box sx={{ ...orbSx, width: 420, height: 420, top: "-120px", left: "-100px", background: "radial-gradient(circle, rgba(200,160,84,0.55), transparent 68%)", animation: `${floatA} 14s ease-in-out infinite` }} />
      <Box sx={{ ...orbSx, width: 520, height: 520, bottom: "-180px", right: "-140px", background: "radial-gradient(circle, rgba(244,233,201,0.35), transparent 68%)", animation: `${floatB} 18s ease-in-out infinite` }} />
      <Box sx={{ ...orbSx, width: 260, height: 260, top: "55%", left: "62%", background: "radial-gradient(circle, rgba(200,160,84,0.30), transparent 70%)", animation: `${floatA} 11s ease-in-out infinite reverse` }} />
      {[0, 1, 2, 3, 4, 5].map((n) => (
        <Box key={n} sx={{ position: "absolute", ...sparkSx, left: `${12 + n * 15}%`, animationDelay: `${n * 1.3}s`, animationDuration: `${7 + (n % 3) * 2}s` }} />
      ))}

      {/* wider than the sign in card: there are more fields */}
      <Box sx={{ ...cardSx, maxWidth: 520, py: { xs: 2, sm: 2.25 }, maxHeight: "calc(100vh - 24px)", overflowY: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
        <Box sx={{ ...stagger(0.1), textAlign: "center", mb: 0.5 }}>
          <Box
            component="img"
            src="/img/tvfmlogo1.png"
            alt="TVFM Logo"
            sx={{ height: 34, width: "auto", cursor: "pointer", animation: `${pulse} 4s ease-in-out infinite` }}
            onClick={() => navigate("/")}
          />
        </Box>

        <Box sx={stagger(0.15)}>
          <SwapTabs
            value={accountType}
            onChange={handleSwap}
            options={[
              { value: "customer", label: "Customer", icon: <PersonIcon sx={{ fontSize: 18 }} /> },
              { value: "business", label: "Business", icon: <BusinessIcon sx={{ fontSize: 18 }} /> },
            ]}
          />
        </Box>

        <Box key={accountType} sx={{ ...stagger(0.05), textAlign: "center", mb: 0.75 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#111", lineHeight: 1.3 }}>
            {isCustomer ? "Create your account" : "Create business account"}
          </Typography>
          <Box sx={{ width: 44, height: 3, borderRadius: 2, bgcolor: "#C8A054", mx: "auto", mt: 0.6, animation: `${growLine} 0.8s 0.2s both ease-out` }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: "0.78rem", "@media (max-height: 720px)": { display: "none" } }}>
            {isCustomer ? "Join to shop, save favourites and track orders" : "For salons, retailers and other businesses"}
          </Typography>
        </Box>

        {apiError && (
          <Typography key={apiError} color="error" variant="body2" align="center" sx={{ mb: 1.5, bgcolor: "#ffebee", p: 1, borderRadius: 2, animation: `${shake} 0.45s ease` }}>
            {apiError}
          </Typography>
        )}

        <SwapPanes index={isCustomer ? 0 : 1}>
          <CustomerSignupForm onSignedUp={handleCustomerSignedUp} onError={setApiError} />
          <BusinessSignupForm onDone={() => navigate("/business/signin")} onError={setApiError} />
        </SwapPanes>

        <Typography variant="body2" align="center" sx={{ ...stagger(0.4), mt: 1, color: "text.secondary", fontSize: "0.82rem" }}>
          Already have an account?{" "}
          <Link href={isCustomer ? "/signin" : "/business/signin"} underline="hover" sx={{ color: "#8a6a24", fontWeight: 700 }}>
            Sign in
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}

export default Signup;
